import { Location } from 'history';
import { Dispatch } from 'react';

import { BaseRow } from '@sbu/components.datagrid';
import { AsideState } from '@sbu/components.datagridaside';

export const DiscardPromptingActionTypes = [
  'ADD',
  'EDIT',
  'SELECT',
  'SELECT_MULTIPLE',
  'CLOSE_ASIDE',
  'NAVIGATE_AWAY',
] as const;
export type DiscardPromptingActionType =
  (typeof DiscardPromptingActionTypes)[number];

export type DiscardPromptingAction<Row extends BaseRow> =
  | { type: 'ADD' }
  | { type: 'EDIT'; row: Row }
  | { type: 'SELECT'; row: Row }
  | { type: 'SELECT_MULTIPLE'; rows?: Row[] }
  | { type: 'CLOSE_ASIDE' }
  | { type: 'NAVIGATE_AWAY'; nextLocation: Location };

export const DataGridPageActionTypes = [
  ...DiscardPromptingActionTypes,
  'EXPORT',
  'DELETE',
  'SAVE',
  'CONFIRM_DELETE',
  'CANCEL_DELETE',
  'CONFIRM_DISCARD',
  'CANCEL_DISCARD',
  'UPDATE_ASIDE_FORM',
] as const;
export type DataGridPageActionType = (typeof DataGridPageActionTypes)[number];

export type DataGridPageAction<Row extends BaseRow> =
  | DiscardPromptingAction<Row>
  | { type: 'EXPORT'; row: Row }
  | { type: 'DELETE'; row: Row }
  | { type: 'SAVE'; row: Row }
  | { type: 'CONFIRM_DELETE' }
  | { type: 'CANCEL_DELETE' }
  | { type: 'CONFIRM_DISCARD' }
  | { type: 'CANCEL_DISCARD' }
  | { type: 'UPDATE_ASIDE_FORM'; dirty: boolean }
  | { type: 'REDIRECT'; redirectTo: string };

export function isDiscardPromptingAction<Row extends BaseRow>(
  action: DataGridPageAction<Row>
): action is DiscardPromptingAction<Row> {
  return DiscardPromptingActionTypes.includes(
    action.type as DiscardPromptingActionType
  );
}

export interface DataGridPageState<Row extends BaseRow> {
  asideState: AsideState;
  isAsideOpen: boolean;
  isDeleteDialogOpen: boolean;
  shouldPromptDiscard: boolean;
  isDiscardDialogOpen: boolean;
  selectedRow?: Row;
  selectedRows?: Row[];
  rowToDelete?: Row;
  nextAction?: DiscardPromptingAction<Row>;
  nextLocation?: Location;
  allowRedirect?: boolean;
  redirectTo?: string;
  onAdd: () => void;
  onEdit: (row: Row) => void;
  onExport: (row: Row) => void;
  onClickRow: (row: Row) => void;
  onSelectMultiple: (rows?: Row[]) => void;
  onDelete: (row: Row) => void;
  onConfirmDelete: () => void;
  onCloseAside: () => void;
  onSaveAside: (row: Row) => void;
  onCancelDelete: () => void;
  onChangeAside: (dirty: boolean) => void;
  onCancelDiscard: () => void;
  onConfirmDiscard: () => void;
  onNavigate: (nextLocation: Location) => void;
  redirect: (redirectTo: string) => void;
  dispatch: Dispatch<DataGridPageAction<Row>>;
}

export type DataGridPageReducer<Row extends BaseRow> = (
  state: DataGridPageState<Row>,
  action: DataGridPageAction<Row>
) => DataGridPageState<Row>;
